import { useState } from "react";
import { Loader2, Plus, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useSignedUrl } from "../admin/MediaCard";
import { PhotoViewer } from "../PhotoViewer";
import { StatusBadge } from "../register/UnderReview";
import { checkFile, uploadFile } from "../../lib/upload";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";

function Photo({ p, onDelete, onView }) {
  const { url, failed } = useSignedUrl("profile-photos", p.storage_path);
  return (
    <div data-testid={`my-photo-${p.id}`} onClick={url ? onView : undefined} className={`relative h-32 w-32 overflow-hidden rounded-xl border border-[#D4AF37]/50 bg-[#F4EFEA] ${url ? "cursor-zoom-in" : ""}`}>
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : failed ? <UserRound data-testid={`my-photo-placeholder-${p.id}`} className="m-auto mt-12 h-6 w-6 text-[#D4AF37]" /> : <Loader2 className="m-auto mt-12 h-5 w-5 animate-spin text-[#7A0016]" />}
      {p.is_primary && <span className="absolute left-1 top-1 rounded-full bg-[#7A0016] px-2 py-0.5 text-[10px] font-semibold text-[#F5E6BA]">Profile photo</span>}
      <span className="absolute bottom-1 left-1"><StatusBadge status={p.status} /></span>
      <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(p); }} data-testid={`my-photo-delete-${p.id}`} aria-label="Remove photo" className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-[#A31828] shadow"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

export function PhotoManager({ photos, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const [viewer, setViewer] = useState(null);
  const gallery = photos.filter((p) => !p.is_primary && p.status !== "rejected");
  const add = async (file, primary) => {
    const problem = checkFile(file, "image");
    if (problem) return toast.error(problem);
    setBusy(primary ? "primary" : "gallery");
    try {
      const item = await uploadFile({ file, kind: "image", bucket: "profile-photos", folder: "photos", userId: user.id });
      const { error } = await supabase.rpc("client_add_photo", { p_path: item.path, p_primary: primary });
      if (error) { await supabase.storage.from("profile-photos").remove([item.path]); throw error; }
      toast.success("Photo uploaded. It will appear after our team approves it.");
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const del = async (p) => {
    const { data: path, error } = await supabase.rpc("client_delete_photo", { p_photo_id: p.id });
    if (error) return toast.error(error.message);
    await supabase.storage.from("profile-photos").remove([path]);
    onChanged();
  };
  const picker = (primary, label, testId) => (
    <label className={`flex h-32 w-32 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-[#D4AF37]/70 bg-white text-xs font-semibold text-[#7A0016] hover:bg-[#F5E6BA]/40 ${busy ? "pointer-events-none opacity-60" : ""}`}>
      {busy === (primary ? "primary" : "gallery") ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
      {label}
      <input type="file" hidden accept="image/jpeg,image/png,image/webp" data-testid={testId} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) add(f, primary); }} />
    </label>
  );
  return (
    <section className="rmm-card p-5 sm:p-6" data-testid="photo-manager">
      <h3 className="font-display text-2xl font-semibold text-[#7A0016]">My photos</h3>
      <p className="mb-4 text-xs text-[#8C6A6C]">1 profile photo and up to 3 gallery photos. New photos are reviewed by our team before matches can see them.</p>
      <div className="flex flex-wrap gap-3">
        {photos.map((p, k) => <Photo key={p.id} p={p} onDelete={del} onView={() => setViewer(k)} />)}
        {picker(true, "Change profile photo", "my-photo-primary-input")}
        {gallery.length < 3 && picker(false, "Add gallery photo", "my-photo-gallery-input")}
      </div>
      {viewer !== null && <PhotoViewer photos={photos} index={viewer} onClose={() => setViewer(null)} />}
    </section>
  );
}
